import type { ApolloClient } from '@apollo/client';
import type { Education } from '@seek/ca-myprofile-utils';
import type { RoleType } from '@seek/profile-read-ui';
import createTrackingClient from '@seek/seek-impression-tracking';
import Cookies from 'js-cookie';

import {
  IMPRESSION_TRACKING_ENABLED,
  IMPRESSION_TRACKING_URL,
} from 'src/config';
import { log } from 'src/modules/error-logger';

import { getCandidateIdFromCache } from '../../components/Profile/data.ts';

type TrackingData =
  | { type: 'role'; data: RoleType }
  | { type: 'qualification'; data: Education };

const TIMED_REQUEST_INTERVAL_MILLISECONDS = 500;
const windowDefined = typeof window !== 'undefined';
const sessionId = windowDefined ? Cookies.get('JobseekerSessionId') : null;

const trackingClient =
  windowDefined && IMPRESSION_TRACKING_ENABLED
    ? createTrackingClient(IMPRESSION_TRACKING_URL, {
        params: sessionId ? { sessionId } : {},
        errorLogger: (message) =>
          log(new Error(message), {
            name: 'trackingFailure',
          }),
        // @ts-expect-error field isn't present in the type, leaving it here just in case
        timedRequestInterval: TIMED_REQUEST_INTERVAL_MILLISECONDS,
      })
    : undefined;

const noop = () => {};

export const trackUnconfirmedDataNudgeImpression =
  trackingClient?.trackUnconfirmedDataNudgeImpression || noop;

const getCompletionDate = (data: Education) => {
  if (!data.completionDate) return {};

  if (data.completed) {
    return {
      completionDate: {
        year: data.completionDate.year,
      },
    };
  }

  return {
    completionDate: {
      year: data.completionDate.year,
      month: data.completionDate.month,
    },
  };
};

export const trackUnconfirmedImpression = (
  { type, data }: TrackingData,
  apolloClient: ApolloClient<any>,
) => {
  const candidateId = getCandidateIdFromCache(apolloClient.cache);

  if (candidateId && sessionId) {
    const resource =
      type === 'role'
        ? {
            careerHistory: {
              id: data.id,
              company: { name: data.company.text },
              role: { title: data.title.text },
              startDate: data.from,
              endDate: data.to,
            },
          }
        : {
            education: {
              id: data.id,
              name: data.name.text,
              institute: {
                name: data.institute.text,
                ontologyId: data.institute.ontologyId,
              },
              highlights: data.highlights,
              completed: data.completed,
              ...getCompletionDate(data),
            },
          };

    trackUnconfirmedDataNudgeImpression({
      candidateId,
      sessionId,
      servedNudgeId: data.tracking?.events?.find(
        (item) => item.key === 'served',
      )?.value,
      resource,
      page: 'My Profile',
    });
  }
};
